//FleetX-Security-CRUD/web-admin/src/pages/Dashboard.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "../api/axios";
import AdminShell from "../components/AdminShell";
import { countCurrentMonitoringAlerts } from "../components/GpsFleetDashboard";
import { useAuth } from "../context/AuthContext";
import useMonitoringNotificationRefresh from "../hooks/useMonitoringNotificationRefresh";
import "../styles/admin.css";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [dashboardActions, setDashboardActions] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [monitoringFleet, setMonitoringFleet] = useState(null);
  const [msg, setMsg] = useState("");
  const [datePreset, setDatePreset] = useState("this_month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [bookingTab, setBookingTab] = useState("upcoming");

  const isAdmin = user?.role === "technical_admin" || user?.role === "admin";
  const isStaff = user?.role === "staff";
  const canAccessPage = isAdmin || isStaff;

  const loadDashboardData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setMsg("");

    try {
      const requests = [
        api.get("/stats"),
        api.get("/admin/dashboard/summary"),
        api.get("/bookings"),
        api.get("/gps/fleet/live", {
          params: { page: 1, limit: 1000, source: "physical" },
        }).catch(() => null),
      ];

      if (isAdmin) {
        requests.push(api.get("/admin/vehicles"));
      }

      const results = await Promise.all(requests);

      const statsRes = results[0];
      const actionRes = results[1];
      const bookingsRes = results[2];
      const monitoringRes = results[3];
      const vehiclesRes = isAdmin ? results[4] : null;

      setStats(statsRes.data || {});
      setDashboardActions(actionRes.data || null);
      setBookings(Array.isArray(bookingsRes.data?.bookings) ? bookingsRes.data.bookings : []);
      setMonitoringFleet(monitoringRes?.data || null);
      setVehicles(
        Array.isArray(vehiclesRes?.data?.vehicles)
          ? vehiclesRes.data.vehicles
          : Array.isArray(vehiclesRes?.data)
          ? vehiclesRes.data
          : []
      );
    } catch (err) {
      if (!silent) {
        setMsg(
          err?.response?.data?.message || "Failed to load dashboard data."
        );
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    if (canAccessPage) {
      loadDashboardData();
    }
  }, [canAccessPage, loadDashboardData]);

  useEffect(() => {
    if (!canAccessPage) return undefined;

    const timer = window.setInterval(() => {
      loadDashboardData({ silent: true });
    }, 15000);

    return () => window.clearInterval(timer);
  }, [canAccessPage, loadDashboardData]);

  const rentedVehicles =
    stats &&
    typeof stats.activeVehicles === "number" &&
    typeof stats.availableVehicles === "number"
      ? Math.max(stats.activeVehicles - stats.availableVehicles, 0)
      : 0;

  const maintenanceVehicles = useMemo(() => {
    if (!Array.isArray(vehicles) || vehicles.length === 0) return 0;
    return vehicles.filter(
      (item) => String(item?.status || "").toLowerCase() === "maintenance"
    ).length;
  }, [vehicles]);

  const dateRange = useMemo(() => {
    const now = new Date();
    const startOfDay = (date) => {
      const next = new Date(date);
      next.setHours(0, 0, 0, 0);
      return next;
    };
    const endOfDay = (date) => {
      const next = new Date(date);
      next.setHours(23, 59, 59, 999);
      return next;
    };

    if (datePreset === "today") {
      return { start: startOfDay(now), end: endOfDay(now), label: "today" };
    }

    if (datePreset === "this_week") {
      const start = startOfDay(now);
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      return { start, end: endOfDay(now), label: "this week" };
    }

    if (datePreset === "this_year") {
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: endOfDay(now),
        label: "this year",
      };
    }

    if (datePreset === "custom") {
      const start = customStartDate ? startOfDay(new Date(`${customStartDate}T00:00:00`)) : null;
      const end = customEndDate ? endOfDay(new Date(`${customEndDate}T00:00:00`)) : null;
      return {
        start: start && !Number.isNaN(start.getTime()) ? start : null,
        end: end && !Number.isNaN(end.getTime()) ? end : null,
        label: "for selected period",
      };
    }

    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: endOfDay(now),
      label: "this month",
    };
  }, [customEndDate, customStartDate, datePreset]);

  const getBookingTime = (booking) => {
    const parsed = new Date(booking?.createdAt || booking?.updatedAt || booking?.startDate || 0);
    return parsed.getTime();
  };

  const periodBookings = useMemo(() => {
    return bookings.filter((item) => {
      const time = getBookingTime(item);
      if (!Number.isFinite(time) || time <= 0) return false;
      if (dateRange.start && time < dateRange.start.getTime()) return false;
      if (dateRange.end && time > dateRange.end.getTime()) return false;
      return true;
    });
  }, [bookings, dateRange]);

  const getStatusKey = (booking) => {
    const status = String(booking?.status || "").toLowerCase();
    if (["pending_payment", "awaiting_payment", "invoice_issued"].includes(status)) {
      return "awaiting_payment";
    }
    if (
      [
        "payment_submitted",
        "payment_verification",
        "payment_under_review",
        "pending_payment_verification",
        "under_review",
      ].includes(status)
    ) {
      return "payment_verification";
    }
    if (["approved", "booked_confirmed", "confirmed"].includes(status)) {
      return "confirmed";
    }
    if (["active", "ongoing"].includes(status)) return "active";
    if (status === "completed") return "completed";
    if (["draft", "pending_verification", "ready_to_continue"].includes(status)) {
      return "in_progress";
    }
    if (["rejected", "cancelled", "cancelled_conflict", "expired", "payment_rejected"].includes(status)) {
      return "cancelled";
    }
    return "booking_review";
  };

  const periodCounts = useMemo(() => {
    return periodBookings.reduce(
      (counts, item) => {
        const key = getStatusKey(item);
        counts[key] = (counts[key] || 0) + 1;
        return counts;
      },
      {
        booking_review: 0,
        awaiting_payment: 0,
        payment_verification: 0,
        confirmed: 0,
        completed: 0,
      }
    );
  }, [periodBookings]);

  const completedBookings = useMemo(() => {
    return periodCounts.completed || 0;
  }, [periodCounts]);

  const totalRevenue = useMemo(() => {
    return periodBookings.reduce((sum, item) => {
      const status = String(item?.status || "").toLowerCase();
      const total = Number(item?.totalPrice || 0);

      if (["approved", "booked_confirmed", "confirmed", "completed"].includes(status)) {
        return sum + total;
      }
      return sum;
    }, 0);
  }, [periodBookings]);

  const recentBookings = useMemo(() => {
    return [...periodBookings]
      .sort((a, b) => {
        const dateA = new Date(a?.createdAt || a?.startDate || 0).getTime();
        const dateB = new Date(b?.createdAt || b?.startDate || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 8);
  }, [periodBookings]);

  const topCustomers = useMemo(() => {
    const grouped = {};

    periodBookings.forEach((item) => {
      const name =
        item?.fullName ||
        item?.name ||
        item?.customerName ||
        "Unknown Customer";

      const total = Number(item?.totalPrice || 0);

      if (!grouped[name]) {
        grouped[name] = {
          name,
          bookings: 0,
          total: 0,
        };
      }

      grouped[name].bookings += 1;
      grouped[name].total += total;
    });

    return Object.values(grouped)
      .sort((a, b) => b.total - a.total)
      .slice(0, 4);
  }, [periodBookings]);

  const fleetTotal = stats?.totalVehicles || vehicles.length || 0;
  const availableCount = stats?.availableVehicles || 0;
  const rentedCount = rentedVehicles;
  const maintenanceCount = maintenanceVehicles;
  const awaitingPaymentCount = periodCounts.awaiting_payment || 0;
  const paymentReviewCount = periodCounts.payment_verification || 0;
  const liveMonitoringAlertCount = useMemo(() => monitoringFleet
    ? (Number.isSafeInteger(monitoringFleet.summary?.attention)
      ? monitoringFleet.summary.attention
      : countCurrentMonitoringAlerts(monitoringFleet.items, bookings))
    : null, [bookings, monitoringFleet]);
  useMonitoringNotificationRefresh(liveMonitoringAlertCount);

  const getPercent = (count) => {
    if (!fleetTotal) return 0;
    return Math.min((count / fleetTotal) * 100, 100);
  };

  const formatPeso = (value) => {
    return `₱${Number(value || 0).toLocaleString()}`;
  };

  const humanizeStatus = (status) =>
    String(status || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase()) || "Pending";

  const goToBookings = (statusFilter = "all", extraState = {}) => {
    navigate("/bookings", {
      state: {
        statusFilter,
        ...extraState,
        dashboardDateFilter: {
          preset: datePreset,
          startDate: customStartDate,
          endDate: customEndDate,
        },
      },
    });
  };

  const goToVehicles = (vehicleStatusFilter = "all") => {
    navigate("/vehicles", { state: { vehicleStatusFilter } });
  };

  const goToAnalytics = (activeTab = "business") => {
    navigate("/analytics-reports", { state: { activeTab } });
  };

  const goToUsers = (extraState = {}) => {
    navigate("/users", { state: extraState });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const safeFormat = (date) =>
      date && !Number.isNaN(date.getTime())
        ? date.toISOString().slice(0, 10)
        : "N/A";

    return `${safeFormat(start)} to ${safeFormat(end)}`;
  };

  const getStatusBadgeClass = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (["approved", "confirmed", "booked_confirmed"].includes(normalized)) return "badge badge-approved";
    if (["rejected", "cancelled", "cancelled_conflict", "expired", "payment_rejected"].includes(normalized)) return "badge badge-rejected";
    if (normalized === "completed") return "badge badge-rented";
    if (["active", "ongoing"].includes(normalized)) return "badge badge-approved";
    return "badge badge-pending";
  };

  const actionCounts = dashboardActions?.counts || {};
  const actionSummary = dashboardActions?.summary || {};
  const actionLists = dashboardActions?.lists || {};

  const attentionCards = [
    {
      key: "liveMonitoringAlerts",
      title: "Live Monitoring Alerts",
      count: liveMonitoringAlertCount,
      loaded: liveMonitoringAlertCount !== null,
      suffix: "active",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M12 2a8 8 0 0 0-8 8c0 5.7 8 12 8 12s8-6.3 8-12a8 8 0 0 0-8-8Zm0 11.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Zm-1-6h2v3h-2v-3Zm0 4h2v2h-2v-2Z"/>
        </svg>
      ),
      button: "Review monitoring",
      onClick: () => navigate("/gps-tracking"),
    },
    {
      key: "paymentVerification",
      title: "Payment Verification",
      count: actionCounts.paymentVerification,
      suffix: "pending",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 14H4v-6h16v6Zm0-10H4V6h16v2Z"/>
        </svg>
      ),
      button: "Review Payments",
      onClick: () => goToBookings("payment_verification"),
    },
    {
      key: "extensionRequests",
      title: "Extension Requests",
      count: actionCounts.extensionRequests,
      suffix: "pending",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M17 12h-5v5h5v-5ZM16 1v2H8V1H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-1V1h-2Zm3 18H5V8h14v11Z"/>
        </svg>
      ),
      button: "Review Extensions",
      onClick: () => goToBookings("extension_requests"),
    },
    {
      key: "additionalPayments",
      title: "Additional Payments",
      count: actionCounts.additionalPayments,
      suffix: "open",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <text x="12" y="17" textAnchor="middle" fontSize="16" fontWeight="700" fontFamily="Arial, sans-serif">₱</text>
        </svg>
      ),
      button: "Review Adjustments",
      onClick: () => goToBookings("additional_payments"),
    },
    {
      key: "pendingRouteReview",
      title: "Pending Route Review",
      count: actionCounts.pendingRouteReview,
      suffix: "for review",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Zm0 9.7A2.7 2.7 0 1 1 12 6.3a2.7 2.7 0 0 1 0 5.4Z"/>
        </svg>
      ),
      button: "Review Routes",
      onClick: () => goToBookings("all", { quickFilter: "route_review" }),
    },
    ...(isAdmin
      ? [
          {
            key: "expiringVerifications",
            title: "Expiring Verifications",
            count: actionCounts.expiringVerifications,
            suffix: "within 30 days",
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4Zm-1 14-3-3 1.41-1.41L11 12.17l4.59-4.58L17 9l-6 6Z"/>
              </svg>
            ),
            button: "Review Accounts",
            onClick: () => goToUsers({ verificationFilter: "expiring" }),
          },
        ]
      : []),
  ];

  const urgentActionTotal = attentionCards
    .reduce((sum, card) => sum + Number(card.count || 0), 0);

  const formatDateTime = (date, time = "") => {
    if (!date) return "N/A";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    const dateText = parsed.toLocaleDateString("en-PH", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return time ? `${dateText} ${time}` : dateText;
  };

  const openBookingDetails = (booking, fallbackFilter = "all") => {
    navigate("/bookings", {
      state: {
        statusFilter: fallbackFilter,
        bookingId: booking?._id,
      },
    });
  };

  const activeTabBookings = useMemo(() => {
    if (bookingTab === "recent") return recentBookings;
    if (bookingTab === "upcoming") return actionLists.upcomingRentals || [];
    if (bookingTab === "active") return actionLists.activeRentals || [];
    return [];
  }, [bookingTab, recentBookings, actionLists]);

  // activeRentals from backend: confirmed/paid bookings whose date range overlaps today.
  // This matches Bookings.jsx filter "active" (isActiveRental) and the booking list destination.
  const vehiclesOutTodayCount = dashboardActions ? (actionCounts.activeRentals ?? 0) : null;

  const kpiCards = [
    {
      key: "revenue",
      label: "Total Revenue",
      value: stats ? formatPeso(totalRevenue) : "...",
      meta: "Paid revenue this month",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <text x="12" y="17" textAnchor="middle" fontSize="16" fontWeight="700" fontFamily="Arial, sans-serif">₱</text>
        </svg>
      ),
      onClick: () => goToAnalytics("revenue"),
    },
    {
      key: "vehicles",
      label: "Total Vehicles",
      value: stats ? stats.totalVehicles : "...",
      meta: "Registered fleet units",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M5.2 7.5 6.4 4h11.2l1.2 3.5 1.7.5c.9.3 1.5 1.1 1.5 2.1V17h-2v3h-3v-3H7v3H4v-3H2v-6.9c0-1 .6-1.8 1.5-2.1l1.7-.5ZM7.8 6 7 8h10l-.8-2H7.8ZM5 13a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm14 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/>
        </svg>
      ),
      onClick: () => navigate("/vehicles"),
    },
    {
      key: "available",
      label: "Available Fleet",
      value: stats ? availableCount : "...",
      meta: "Ready for booking",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm-2 14.5-4.5-4.5 1.41-1.41L10 13.67l7.59-7.59L19 7.5l-9 9Z"/>
        </svg>
      ),
      onClick: () => goToVehicles("available"),
    },
    {
      key: "vehicles_out_today",
      label: "Vehicles Out Today",
      value: vehiclesOutTodayCount !== null ? vehiclesOutTodayCount : "...",
      meta: "Currently active rentals",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2Zm1 14.5-4.5-4.5 1.41-1.41L13 13.67V7h2v7.5l-2 2Z"/>
        </svg>
      ),
      onClick: () => goToBookings("active"),
    },
    {
      key: "awaiting",
      label: "Awaiting Payment",
      value: stats ? awaitingPaymentCount : "...",
      meta: "Invoices waiting this month",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M11 17h2v-6h-2v6Zm1-8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm0-7C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8Z"/>
        </svg>
      ),
      onClick: () => goToBookings("awaiting_payment"),
    },
    {
      key: "payment_review",
      label: "Payment Review",
      value: stats ? paymentReviewCount : "...",
      meta: "Proofs submitted this month",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4Zm-1 14-3-3 1.41-1.41L11 12.17l4.59-4.58L17 9l-6 6Z"/>
        </svg>
      ),
      onClick: () => goToBookings("payment_verification"),
    },
  ];

  if (!canAccessPage) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <AdminShell
      eyebrow="OVERVIEW"
      title="Dashboard"
      subtitle="Monitor today's operations, pending actions, and fleet activity."
      hideTopbarCopy
      pageClassName="admin-shell--dashboard"
      actions={
        <div className="dashboard-actions">
          <div className="dashboard-date-filter" aria-label="Dashboard date filter">
            <select
              value={datePreset}
              onChange={(event) => setDatePreset(event.target.value)}
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="this_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {datePreset === "custom" ? (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(event) => setCustomStartDate(event.target.value)}
                  aria-label="Dashboard start date"
                />
                <input
                  type="date"
                  value={customEndDate}
                  min={customStartDate}
                  onChange={(event) => setCustomEndDate(event.target.value)}
                  aria-label="Dashboard end date"
                />
              </>
            ) : null}
          </div>
        </div>
      }
    >
      {msg ? <div className="dashboard-alert">{msg}</div> : null}

      {/* KPI Summary */}
      <section className="dashboard-kpi-section">
        <div className="dashboard-section-head">
          <h3 className="dashboard-section-title">KPI Summary</h3>
          <p className="dashboard-section-copy">Track your current fleet, payment, and revenue snapshot.</p>
        </div>
        <div className="dashboard-kpi-grid">
          {kpiCards.map((card) => (
            <button
              key={card.key}
              type="button"
              className="dashboard-kpi-card"
              onClick={card.onClick}
            >
              <div className="dashboard-kpi-card__icon">{card.icon}</div>
              <div className="dashboard-kpi-card__body">
                <div className="dashboard-kpi-card__label">{card.label}</div>
                <div className="dashboard-kpi-card__value">{card.value}</div>
                <div className="dashboard-kpi-card__meta">{card.meta}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Needs Attention */}
      <section className="dashboard-attention-section">
        <div className="dashboard-section-head">
          <h3 className="dashboard-section-title">Needs Attention</h3>
          <p className="dashboard-section-copy">Admin actions that may require immediate review.</p>
        </div>

        {dashboardActions && monitoringFleet && urgentActionTotal === 0 ? (
          <div className="dashboard-clear-state">All clear. No urgent actions right now.</div>
        ) : null}

        <div className="dashboard-attention-grid">
          {attentionCards.map((card) => (
            <article key={card.key} className="dashboard-action-card dashboard-action-card--urgent">
              <div className="dashboard-action-card__icon">{card.icon}</div>
              <div className="dashboard-action-card__top-row">
                <span className="dashboard-action-card__label">{card.title}</span>
              </div>
              <div className="dashboard-action-card__count-row">
                <strong>{(card.loaded ?? Boolean(dashboardActions))
                  ? Number(card.count || 0).toLocaleString()
                  : "..."}</strong>
                <small>{card.suffix}</small>
              </div>
              <button type="button" className="dashboard-action-card__button" onClick={card.onClick}>
                {card.button}
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Bookings Overview + Fleet Status */}
      <div className="dashboard-main-grid">
        {/* Bookings Overview */}
        <div className="dashboard-panel dashboard-panel--table">
          <div className="dashboard-panel__head">
            <h3 className="dashboard-panel__title">Bookings Overview</h3>
            <button
              type="button"
              className="dashboard-panel__link dashboard-panel__link--button"
              onClick={() => goToBookings("all")}
            >
              View all bookings →
            </button>
          </div>

          <div className="dashboard-tabs">
            <button
              type="button"
              className={`dashboard-tab${bookingTab === "recent" ? " dashboard-tab--active" : ""}`}
              onClick={() => setBookingTab("recent")}
            >
              Recent
            </button>
            <button
              type="button"
              className={`dashboard-tab${bookingTab === "upcoming" ? " dashboard-tab--active" : ""}`}
              onClick={() => setBookingTab("upcoming")}
            >
              Upcoming
            </button>
            <button
              type="button"
              className={`dashboard-tab${bookingTab === "active" ? " dashboard-tab--active" : ""}`}
              onClick={() => setBookingTab("active")}
            >
              Active
            </button>
          </div>

          {activeTabBookings.length === 0 ? (
            <div className="dashboard-empty-cell">
              {bookingTab === "recent" && "No recent bookings found."}
              {bookingTab === "upcoming" && "No upcoming rentals found."}
              {bookingTab === "active" && "No active rentals right now."}
            </div>
          ) : (
            <div className="dashboard-table-wrap">
              <table className="dashboard-table dashboard-table--compact">
                <thead>
                  <tr>
                    <th>Date / Time</th>
                    <th>Customer</th>
                    <th>Vehicle</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {activeTabBookings.map((item) => {
                    const customerName =
                      item?.customer ||
                      item?.fullName ||
                      item?.name ||
                      item?.customerName ||
                      "Unknown Customer";

                    const vehicleText =
                      item?.vehicleName ||
                      (item?.vehicleId?.make && item?.vehicleId?.model
                        ? `${item.vehicleId.make} ${item.vehicleId.model}`
                        : item?.vehicle ||
                          item?.vehicleId?.plateNo ||
                          "Vehicle");

                    const dateDisplay =
                      item?.startDate
                        ? formatDateTime(item.startDate, item.startTime || "")
                        : formatDateTime(item?.createdAt || item?.updatedAt);

                    const destination =
                      item?.destination ||
                      item?.dropoffLocation ||
                      "N/A";

                    const amount =
                      item?.totalPrice != null
                        ? formatPeso(item.totalPrice)
                        : "—";

                    return (
                      <tr
                        key={item?._id}
                        style={{ cursor: "pointer" }}
                        onClick={() => openBookingDetails(item, bookingTab === "active" ? "active" : bookingTab === "upcoming" ? "confirmed" : "all")}
                      >
                        <td style={{ whiteSpace: "nowrap" }}>{dateDisplay}</td>
                        <td>{customerName}</td>
                        <td>{vehicleText}</td>
                        <td
                          title={destination}
                          style={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {destination}
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(item?.status)}>
                            {humanizeStatus(item?.status)}
                          </span>
                        </td>
                        <td>
                          <span className="dashboard-amount">{amount}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fleet Status */}
        <div className="dashboard-panel dashboard-fleet-panel">
          <div className="dashboard-panel__head">
            <h3 className="dashboard-panel__title">Fleet Status</h3>
          </div>

          <div className="dashboard-progress-list">
            <button
              type="button"
              className="dashboard-progress-item dashboard-progress-item--clickable"
              onClick={() => goToVehicles("available")}
              title="View available vehicles"
            >
              <div className="dashboard-progress-item__row">
                <span>Available</span>
                <span>{availableCount}/{fleetTotal || 0}</span>
              </div>
              <div className="dashboard-progress">
                <div
                  className="dashboard-progress__bar success"
                  style={{ width: `${getPercent(availableCount)}%` }}
                />
              </div>
            </button>

            <button
              type="button"
              className="dashboard-progress-item dashboard-progress-item--clickable"
              onClick={() => goToVehicles("ongoing")}
              title="View rented vehicles"
            >
              <div className="dashboard-progress-item__row">
                <span>Rented</span>
                <span>{rentedCount}/{fleetTotal || 0}</span>
              </div>
              <div className="dashboard-progress">
                <div
                  className="dashboard-progress__bar warning"
                  style={{ width: `${getPercent(rentedCount)}%` }}
                />
              </div>
            </button>

            <button
              type="button"
              className="dashboard-progress-item dashboard-progress-item--clickable"
              onClick={() => goToVehicles("maintenance")}
              title="View vehicles under maintenance"
            >
              <div className="dashboard-progress-item__row">
                <span>Maintenance</span>
                <span>{maintenanceCount}/{fleetTotal || 0}</span>
              </div>
              <div className="dashboard-progress">
                <div
                  className="dashboard-progress__bar neutral"
                  style={{ width: `${getPercent(maintenanceCount)}%` }}
                />
              </div>
            </button>
          </div>

          <button
            type="button"
            className="dashboard-fleet-footer dashboard-fleet-footer--clickable"
            onClick={() => goToVehicles("all")}
            title="View all vehicles"
          >
            <div className="dashboard-fleet-footer__icon">
              <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                <path d="M5.2 7.5 6.4 4h11.2l1.2 3.5 1.7.5c.9.3 1.5 1.1 1.5 2.1V17h-2v3h-3v-3H7v3H4v-3H2v-6.9c0-1 .6-1.8 1.5-2.1l1.7-.5ZM7.8 6 7 8h10l-.8-2H7.8ZM5 13a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm14 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/>
              </svg>
            </div>
            <div>
              <div className="dashboard-fleet-footer__label">Total Fleet</div>
              <div className="dashboard-fleet-footer__value">{fleetTotal} Vehicles</div>
            </div>
          </button>
        </div>
      </div>
    </AdminShell>
  );
}
